<template>
    <div v-if="error" class="content-container py-5">
        <alert>
            {{ error }}
            <ui-button class="ms-3" @click="requestData">
                Try Again
            </ui-button>
        </alert>
    </div>
    <div v-if="property">
        <public-property-header :property="property" :galleryMedia="galleryMedia" @openGallery="openGallery" class="my-4" />

        <div class="position-relative pt-3">
            <div class="sticky-top-tabs-nav" :style="stickyNavTopStyle" ref="propertyNav">
                <div class="nav-filler"></div>
                <div class="content-container pb-4 pt-2 pb-lg-0 pt-lg-0 d-flex align-items-center position-relative">
                    <public-property-nav :property="property" :showAvailability="!inquireOnly" :active="navPosition" @goTo="moveToSection" class="ps-1 ps-md-3 ps-lg-0" :showReviews="!thirdPartyRates" :showRooms="showRoomsAndIcons" />

                    <div v-if="showQuoteInNav" class="ms-auto position-relative d-flex align-items-center pb-3">

                        <ui-button v-if="inquireOnly" class="text-nowrap" @click="showInquiryModal=true">
                            Inquire for rates
                        </ui-button>

                        <template v-else-if="!canReserve">
                            
                            <template v-if="avgHeaderLabel">
                                <div>
                                    <span class="me-2">Avg. </span><h4 class="me-1 d-inline mb-0">{{ avgHeaderLabel }}</h4>/night
                                </div>
                                <ui-button @click="() => moveToSection('availability')" class="rounded-2 ms-2" size="sm" :loading="loadingQuotes || buttonsLoading" :disabled="loadingQuotes || buttonsLoading">
                                    <i class="fas fa-calendar-alt"></i>
                                </ui-button>

                            </template>
                            <ui-button v-else class="text-nowrap" @click="showInquiryModal=true">
                                Inquire for rates
                            </ui-button>

                        </template>

                        <template v-else-if="canReserve">

                            <div v-if="!useHotelRates">
                                <h4 class="me-1 d-inline mb-0">{{ money(quotes[property.id].total) }}</h4>
                                <span>/ stay</span>
                            </div>
                            
                            <ui-button class="text-nowrap rounded-2 ms-2" @click="reserveButtonClick" :loading="loadingQuotes || buttonsLoading" :disabled="loadingQuotes || buttonsLoading" size="sm">
                                {{ loadingQuotes ? 'Getting Quote' : reserveButtonLabel }}
                            </ui-button>

                            <ui-button @click="() => moveToSection('availability')" class="rounded-2 ms-2" size="sm" :loading="loadingQuotes || buttonsLoading" :disabled="loadingQuotes || buttonsLoading">
                                <i class="fas fa-calendar-alt"></i>
                            </ui-button>
                        </template>
                    </div>
                </div>
            </div>

            <div class="content-container">

                <container>
                    <row>
                        <column :lg="16" class="position-relative">
                            
                            <hr class="mt-3 mb-5" />

                            <intersect @on="() => setNavPosition('overview')" rootMargin="-50% 0px">
                                <div ref="overviewContainer">
                                    <public-property-top-icons v-if="showRoomsAndIcons" :property="property" />
                                    <hr v-if="showRoomsAndIcons" class="my-5" />
                                    
                                    <public-property-description :property="property" />

                                    <hr class="my-5" />
                                </div>
                            </intersect>
                            
                            
                            <intersect @on="() => setNavPosition('photos')" rootMargin="-50% 0px">
                                <div ref="photosContainer">
                                    <public-property-photos :property="property" :galleryMedia="galleryMedia" @openGallery="openGallery" />

                                    <hr class="my-5" />
                                </div>
                            </intersect>

                            <intersect @on="() => setNavPosition('amenities')" rootMargin="-50% 0px">
                                <div ref="amenitiesContainer">
                                    <public-property-amenities :property="property" />

                                    <hr class="my-5" />
                                </div>
                            </intersect>

                            

                        </column>
                        <column v-if="!showQuoteInNav" :lg="8" class="position-relative ps-lg-5">
                            <card :class="stickyQuotePoisitionCssClass" :style="stickyQuotePositionStyle" v-if="inquireOnly">
                                <template #default>
                                    <card-body class="d-flex d-lg-block justify-content-between">
                                        <div class="me-3 md-5 me-lg-0 mb-0 mb-lg-3">
                                            <h4 class="d-none d-md-block">Inquire for rates</h4>
                                            <card-text :class="uiStore.isMobile ? 'x-small' : 'small'" class="mb-0">
                                                This property requires additional information for a quote. Please inquire for rates.
                                            </card-text>
                                        </div>
                                        <ui-button :block="quoteFormButtonBlock" class="text-nowrap" @click="showInquiryModal=true">
                                            Inquire for rates
                                        </ui-button>
                                    </card-body>
                                    
                                </template>
                            </card>
                            <card :class="stickyQuotePoisitionCssClass" :style="stickyQuotePositionStyle" v-else>
                                <template #default>
                                    <card-body class="d-md-flex w-100 d-lg-block justify-content-start">
                                        <div v-if="!thirdPartyRates && !canReserve" class="align-self-center me-md-5 me-lg-0">
                                            <div v-if="avgHeaderLabel" class="me-3 md-5 me-lg-0 mb-0 mb-lg-3 pb-3 pb-md-0">
                                                <span class="me-2">Avg. </span><h4 class="me-1 d-inline">{{ avgHeaderLabel }}</h4>/night
                                            </div>
                                            <h4 v-else class="me-3 md-5 me-lg-0 mb-0 mb-lg-3 pb-3pb-md-0">
                                                Inquire for rates
                                            </h4>
                                        </div>
                                        <div :class="((!uiStore.isMobile) && (!uiStore.isTablet)) ? 'border rounded-2 mb-lg-3 me-md-5 me-lg-0 flex-grow-1' : null">
                                            <prompt :multiLines="true" placement="bottom" :showButtons="false" title="Select Dates" :peekSize="6" :peekAutoSize="true" :open="calPeekOpen" @hide="onCalPeekClose">
                                                <template #default>
                                                    <div v-if="uiStore.isMobile || uiStore.isTablet" class="position-absolute start-50 top-0"></div>
                                                    <div v-else class="clickable d-flex border-bottom">
                                                        <div class="border-end w-50 p-2">
                                                            <div class="text-uppercase" :class="arrivalDepartureLabelCssClass">Arrival</div>
                                                            <div v-if="calSelectedRange || calStartDate" :class="arrivalDepartureValueCssClass">
                                                                {{ calSelectedRange ? calSelectedRange.start.format('D, n/j/Y') : calStartDate.format('D, n/j/Y') }}
                                                            </div>
                                                            <div v-else class="opacity-50" :class="arrivalDeparturePlaceholderCssClass">
                                                                mm/dd/yyyy
                                                            </div>
                                                        </div>
                                                        <div class="w-50 p-2">
                                                            <div class="text-uppercase" :class="arrivalDepartureLabelCssClass">Departure</div>
                                                            <div v-if="calSelectedRange" :class="arrivalDepartureValueCssClass">
                                                                {{ calSelectedRange.end.format('D, n/j/Y') }}
                                                            </div>
                                                            <div v-else class="opacity-50" :class="arrivalDeparturePlaceholderCssClass">
                                                                mm/dd/yyyy
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                                <template #field>
                                                    <!-- WE HAVE MULTIPLE CALENDARS ON THE SCREEN, UPDATE THE V-BIND and V-ON OBJECTS -->
                                                    <single-range-calendar v-model="calSelectedRange" v-bind="singleRangeCalProps" v-on="singleRangeCalEvents" />
                                                </template>
                                            </prompt>

                                            <prompt :multiLines="true" :showButtons="false" placement="bottom" title="Number of Guests" :peekSize="5" :peekAutoSize="true" @hide="onGuestsNumberClose" :open="occupantsPeekOpen">
                                                <template #default>
                                                    <div v-if="uiStore.isMobile || uiStore.isTablet" class="position-absolute start-50 top-0 clickable"></div>
                                                    <div v-else class="p-2 clickable">
                                                        <div class="text-uppercase" :class="arrivalDepartureLabelCssClass">Guests</div>
                                                        <div :class="arrivalDepartureValueCssClass">{{ guestsNumberLabel }}</div>
                                                    </div>
                                                </template>
                                                <template #field>
                                                    <p class="small text-muted" v-if="property.maxOccupancy">
                                                        Maximum occupancy: {{ property.maxOccupancy }}
                                                    </p>
                                                    <form-group>
                                                        <form-label>Number of Adult(s):</form-label>
                                                        <form-select name="adults-count" :options="numberAdultsOptions" v-model="adults" class="clickable" />
                                                    </form-group>
                                                    <form-group>
                                                        <form-label>Number of Children:</form-label>
                                                        <form-select name="children-count" :options="numberChildrenOptions" v-model="children" class="clickable"/>
                                                    </form-group>
                                                </template>
                                            </prompt>
                                        </div>

                                        <div v-if="canReserve" class="mb-3 mb-md-0 mb-lg-3 me-md-5 me-lg-0 d-flex d-lg-block justify-content-between align-items-center w-100">
                                            <div v-if="!useHotelRates">
                                                <transition v-if="!uiStore.isMobile && !uiStore.isTablet" name="v-slide-transition-3">
                                                    <public-property-quote-fees-list v-if="showQuoteDetails" :property="property" class="text-muted" :quote="quotes[property.id]"/>
                                                </transition>

                                                <div v-if="thirdPartyRates">
                                                    <h2 class="me-1 d-inline mb-0">{{ money(quotes[property.id].total) }}</h2>
                                                    <span>/ stay</span>
                                                </div>
                                                <div v-else class="d-flex align-items-baseline justify-content-between">
                                                    <div>
                                                        <h2 class="me-1 d-inline mb-0">{{ money(quotes[property.id].total) }}</h2>
                                                        <span>/ stay</span>
                                                    </div>

                                                    <i v-if="uiStore.isMobile || uiStore.isTablet" class="fas fa-info-circle clickable ms-2 text-muted" @click="showFeesList"></i>
                                                    <div v-else class="x-small text-muted clickable" @click="toggleFeesList">{{ showQuoteDetails ? 'Hide' : 'View' }} Details</div>
                                                </div>
                                                <div v-if="quotes[property.id].deposit" class="x-small text-muted">
                                                    Plus a refundable security deposit of {{ money(quotes[property.id].deposit) }}
                                                </div>

                                                <template v-if="showQuoteDetails && (uiStore.isMobile || uiStore.isTablet)">
                                                    <peek :open="quoteDetailsOpen" :autoSize="true" :size="6" placement="bottom" :noPadding="true" @close="closeFeesList">
                                                        <template #header>
                                                            Quote Details
                                                        </template>
                                                        <template #default>
                                                            <public-property-quote :property="property" :checkIn="quotesCheckIn" :checkOut="quotesCheckOut" :quote="quotes[property.id]" :showInFull="true" :showHeader="false" :showDeposit="!thirdPartyRates" class="border-0" :includeBookButton="true" @book="reserveButtonClick" :bookButtonLabel="reserveOrRequestLable" :buttonLoading="loadingQuotes || buttonsLoading" />
                                                        </template>
                                                    </peek>
                                                </template>
                                                
                                            </div>
                                            <div v-else-if="hotelQuotesRangeLabel">
                                                <h4 class="me-1 d-inline mb-0">{{ hotelQuotesRangeLabel }}</h4>
                                                <span>/ stay</span>
                                            </div>
                                            
                                            <div v-if="mobileSelectedDatesLabel" class="small clickable text-primary text-end ms-auto" @click="calPeekOpen=true">
                                                {{ mobileSelectedDatesLabel }} <i class="fas fa-edit"></i>
                                            </div>

                                            <div v-if="property.traditional" class="small text-muted mt-2">
                                                Includes the full cost of {{ traditionalMonthsList }}
                                            </div>
                                        </div>
                                        
                                        <div class="d-flex align-items-start ms-auto">
                                            <chat-circle-icon v-if="uiStore.isMobile || uiStore.isTablet" class="me-3 clickable flex-shrink-0 text-muted" :size="40" @click="showInquiryModal=true" title="Inquire for rates"/>

                                            <div v-if="uiStore.isMobile || uiStore.isTablet" class="me-3 clickable flex-shrink-0 text-muted position-relative" title="Number Occupants">
                                                <people-circle-icon :size="40" @click="occupantsPeekOpen=true"/>
                                                <badge v-if="(adults && adults > 1) || children" class="position-absolute top-0 end-0 xx-small" style="margin-top:-5px;">
                                                    <span v-if="adults">
                                                        {{ adults }} <i class="fas fa-user-friends"></i>
                                                    </span>
                                                    <span v-if="children" :class="adults ? 'ms-2': null">
                                                        {{ children }} <i class="fas fa-child"></i>
                                                    </span>
                                                </badge>
                                            </div>
                                        
                                            <ui-button :block="quoteFormButtonBlock" class="text-nowrap" @click="reserveButtonClick" :loading="loadingQuotes || buttonsLoading" :disabled="loadingQuotes || buttonsLoading">
                                                {{ loadingQuotes ? 'Getting Quote' : (canReserve ? reserveButtonLabel : 'Select Dates') }}
                                            </ui-button>
                                        </div>
                                    </card-body>
                                    
                                </template>

                                <template #footer v-if="!inquireOnly && !uiStore.isMobile && !uiStore.isTablet">
                                    <ui-button :outline="true" color="secondary" size="sm" :block="true" @click="showInquiryModal=true">
                                        Inquire about this property
                                    </ui-button>
                                </template>
                            </card>
                        </column>
                    </row>
                </container>
            </div>

            <div class="content-container">

                <intersect v-if="!inquireOnly" @on="() => setNavPosition('availability')" rootMargin="-50% 0px">
                    <div ref="availabilityContainer">
                        <h4 class="mb-3">Availability</h4>

                        <!-- WE HAVE MULTIPLE CALENDARS ON THE SCREEN, UPDATE THE V-BIND and V-ON OBJECTS -->
                        <single-range-calendar v-model="calSelectedRange" v-bind="singleRangeCalPropsInline" v-on="singleRangeCalEvents">
                            <template v-if="!useHotelRates && (canReserve || loadingQuotes)" #buttons>
                                <ui-button class="text-nowrap ms-2" @click="reserveButtonClick" :loading="loadingQuotes || buttonsLoading" :disabled="loadingQuotes || buttonsLoading" size="sm">
                                    {{ loadingQuotes ? 'Getting Quote' : multiCalReserveButtonLabel }}
                                </ui-button>
                            </template>
                        </single-range-calendar>

                        <hr class="my-5" v-if="useHotelRates || uiStore.isMobile || uiStore.isTablet" />
                    </div>

                </intersect>

                <intersect v-if="useHotelRates" @on="() => setNavPosition('hotelRooms')" rootMargin="-50% 0px">
                    
                    <div ref="hotelRoomsContainer">
                        <div v-if="property.canBookEntireHotel" class="mb-4">
                            <h4 class="mb-3">Entire {{ hotelShortLabel }}</h4>

                            <public-property-hotel-room-group-card :hotel="property" :group="entireHotelRoomGroup" :quotes="quotes" :displayRates="displayRates" @bookHotelRoom="startReservation" />
                        </div>

                        <h4 class="mb-3">
                            Individual {{ hotelRoomsLabel }}
                        </h4>
                        <div>

                            <public-property-hotel-room-group-card v-for="(hotelRoomGroup, index) in hotelRoomGroups" :key="`property-${property.id}-hotel-room-${hotelRoomGroup.displayId}-${index}`" :hotel="property" :group="hotelRoomGroup" :quotes="quotes" :displayRates="displayRates" @bookHotelRoom="startReservation" class="mb-3" />
                            
                        </div>

                    </div>

                    <hr class="my-5" />

                </intersect>


                <intersect v-if="!thirdPartyRates" @on="() => setNavPosition('reviews')" rootMargin="-50% 0px">
                    <div ref="reviewsContainer">
                        <public-property-reviews :property="property" />

                        <hr class="my-5" />
                    </div>
                </intersect>
                <divider v-else size="sm"/>

                <intersect v-if="showRoomsAndIcons" @on="() => setNavPosition('rooms')" rootMargin="-50% 0px">
                    <div ref="roomsContainer">
                        <public-property-rooms :property="property" />

                        <hr class="my-5" />
                    </div>
                </intersect>
                
                <intersect @on="() => setNavPosition('location')" rootMargin="-50% 0px">
                    <div ref="locationContainer">

                        <h4 class="mb-3">{{ property.name }} Location</h4>
                        <p class="text-muted">{{ property.city }}, {{ property.state }} {{ (property.country && property.country != 'US') ? ', '+property.country : '' }}</p>
            
                        <public-property-map :property="property" class="rounded-2" />

                        <hr class="my-5" />
                    </div>
                </intersect>

                <intersect  @on="() => setNavPosition('facts')" rootMargin="-50% 0px">
                    <div ref="factsContainer">

                        <h4 class="mb-4">Essential Information</h4>
                        <hr class="my-5" />

                        <template v-if="property.serviceLevel == 'W'">
                            <public-property-essential-info :property="property" />
                            <hr class="mt-3 mb-5" />
                        </template>

                        <template v-if="showTruths">
                            <public-property-truths :property="property" />
                            <hr class="my-5" />
                        </template>

                        <public-property-things-to-know :property="property" :thirdPartyRates="thirdPartyRates" />
                        <hr class="my-5" />

                    </div>
                </intersect>


            </div>

        </div>


        <div class="content-container">
            

            <public-property-related v-if="!thirdPartyRates" :property="property" />

            <divider/>
        </div>

        <vertical-gallery-modal v-if="showGallery" :active="activeGallery" :media="galleryMedia" @close="showGallery=false">
            {{ property.name }}
        </vertical-gallery-modal>
        <public-property-inquire-modal v-if="showInquiryModal" :property="property" @close="showInquiryModal=false" />
    </div>
    <div v-else-if="!error">
        <public-property-header-placeholder />

        <div class="content-container">

            <container>
                <row>
                    <column :lg="16" class="position-relative">
                        <public-property-nav-placeholder />
                        <public-property-description-placeholder class="mt-5" />
                    </column>
                    <column v-if="!uiStore.isMobile && !uiStore.isTablet" :lg="8" class="position-relative ps-lg-5">
                        <card class="bg-light mt-4">
                            <template #default>
                                <card-body>
                                    <divider height="sm"/>
                                    <ui-button :block="true" :placeholder="true" class="rounded-2">Select Dates</ui-button>
                                </card-body>
                            </template>
                        </card>
                    </column>
                </row>
            </container>
        </div>
    </div>
</template>
<script type="text/javascript">
    import PublicPropertyHeader from '@/components/Content/Public/Properties/Header.vue';
    import PublicPropertyNav from '@/components/Content/Public/Properties/Nav.vue';
    import PublicPropertyMap from '@/components/Content/Public/Properties/Map.vue';
    import PublicPropertyTopIcons from '@/components/Content/Public/Properties/TopIcons.vue';
    import PublicPropertyDescription from '@/components/Content/Public/Properties/Description.vue';
    import PublicPropertyPhotos from '@/components/Content/Public/Properties/Photos.vue';
    import PublicPropertyAmenities from '@/components/Content/Public/Properties/Amenities/Amenities.vue';
    import PublicPropertyReviews from '@/components/Content/Public/Properties/Reviews/Reviews.vue';
    import PublicPropertyRooms from '@/components/Content/Public/Properties/Rooms/Rooms.vue';
    import PublicPropertyThingsToKnow from '@/components/Content/Public/Properties/ThingsToKnow/ThingsToKnow.vue';
    import PublicPropertyRelated from '@/components/Content/Public/Properties/RelatedProperties.vue';
    import PublicPropertyInquireModal from '@/components/Content/Public/Properties/InquireModal.vue';
    import PublicPropertyHotelRoomGroupCard from '@/components/Content/Public/Properties/Cards/HotelRoomGroup.vue';
    import PublicPropertyTruths from '@/components/Content/Public/Properties/Truths.vue';
    import PublicPropertyEssentialInfo from '@/components/Content/Public/Properties/EssentialInfo/Info.vue';
    import PublicPropertyQuote from '@/components/Content/Public/Properties/Quote.vue';
    import PublicPropertyQuoteFeesList from '@/components/Content/Public/Properties/QuoteFeesList.vue';

    import PublicPropertyHeaderPlaceholder from '@/components/Content/Public/Properties/HeaderPlaceholder.vue';
    import PublicPropertyNavPlaceholder from '@/components/Content/Public/Properties/NavPlaceholder.vue';
    import PublicPropertyDescriptionPlaceholder from '@/components/Content/Public/Properties/DescriptionPlaceholder.vue';

    import ChatCircleIcon from '@/assets/icons/chat-circle.svg.vue';
    import PeopleCircleIcon from '@/assets/icons/people-circle.svg.vue';

    import CommonComponents from '@/components/Common.vue';

    import DateObject from '@/classes/DateObject.js';
    import DateRange from '@/classes/Ranges/Date.js';
    import UI from '@/classes/UI.js';
    import Media from '@/classes/Media.js';
    import Formatter from '@/classes/Formatter.js';
    import Api from '@/classes/Api.js';
    import Utilities from '@/classes/Utilities.js';
    import FormOption from '@/classes/FormOption.js';
    import MobileApp from '@/classes/MobileApp.js';
    import ApiConfig from '@/config/api.json';

    import Analytics from '@/classes/Analytics.js';

    import { UseApiStore } from '@/store/Api.js';
    import { UseUIStore } from '@/store/UI.js';
    import { UseAuthStore } from '@/store/Auth.js';

    export default {
        components: {
            PublicPropertyHeader,
            PublicPropertyNav,
            PublicPropertyMap,
            PublicPropertyTopIcons,
            PublicPropertyDescription,
            PublicPropertyPhotos,
            PublicPropertyAmenities,
            PublicPropertyReviews,
            PublicPropertyRooms,
            PublicPropertyThingsToKnow,
            PublicPropertyRelated,
            PublicPropertyInquireModal,
            PublicPropertyHotelRoomGroupCard,
            PublicPropertyTruths,
            PublicPropertyEssentialInfo,
            PublicPropertyQuote,
            PublicPropertyQuoteFeesList,

            PublicPropertyHeaderPlaceholder,
            PublicPropertyNavPlaceholder,
            PublicPropertyDescriptionPlaceholder,

            ChatCircleIcon,
            PeopleCircleIcon,

            ...CommonComponents,
        },

        /*
        async beforeRouteEnter(to, from, next) {
            let apiStore = UseApiStore();
            let url = 'properties/'+to.params.slug;

            if (!apiStore.requests[url]) {
                await apiStore.get({
                    url: url,
                    staticList: true,
                });
            }
            next();
        },
        */
       
        setup() {
            return {
                apiStore: UseApiStore(),
                uiStore: UseUIStore(),
                authStore: UseAuthStore(),
            };
        },

        data() {
            return {
                disposed: false,

                error: null,
                requestsTimer: null,
                emptyResults: null,

                calSelectedRange: null,
                calMinDate: null,
                calMaxDate: null,
                calDisplayDate: null,
                calSlotKey: 0,
                calStartDate: null,
                calFullNumberMonths: 3,

                quoteFromCreated: false,

                calPeekOpen: null,
                occupantsPeekOpen: null,

                adults: 1,
                children: 0,

                numberAdultsOptions: null,
                numberChildrenOptions: null,

                buttonsLoading: false,
                loadingQuotes: false,
                quotes: null,
                quotesCheckIn: null,
                quotesCheckOut: null,
                displayRates: false,

                navPosition: 'overview',

                showGallery: false,
                activeGallery: 0,

                singleRangeCalEvents: {
                    monthChange: this.updateDisplayMonth,
                    clear: this.clearSelectedDate,
                    selectStart: this.setStartDate,
                    selectEnd: this.onEndDate,
                },

                showInquiryModal: false,

                showQuoteDetails: false,
                quoteDetailsOpen: true,

                eventRecorded: false,

                initialRequest: false,
                lastQuoteKey: null,
            };
        },

        created() {
            this.setGuestsOptions();

            window.addEventListener('visibilitychange', this.leftPageVisibility);

            this.$watch(
                () => this.$route.params,
                this.setParamAndRequest,
                { immediate: true }
            );
        },

        watch: {
            pageUrl() {
                this.$router.replace(this.pageUrl);
            },
        },

        beforeRouteLeave(to, from) {
            if (
                (to.meta) &&
                (to.meta.multiRoute) &&
                (from.meta) &&
                (from.meta.multiRoute) &&
                (to.meta.multiRoute == from.meta.multiRoute)
            ) {
                return;
            }
            this.disposed = true;
            clearTimeout(this.requestsTimer);

            window.removeEventListener('visibilitychange', this.leftPageVisibility);

            this.leftPage();
        },

        computed: {

            pageUrl() {
                let url = '/properties/'+this.$route.params.slug;
                if (this.$route.params.thirdParty) {
                    url += '/'+this.$route.params.thirdParty;
                }
                let params = {};

                if (this.calSelectedRange) {
                    params.start = this.calSelectedRange.start.format('Y-m-d');
                    params.end = this.calSelectedRange.end.format('Y-m-d');
                }

                if ((this.adults) && (this.adults > 1)) {
                    params.adults = this.adults;
                }

                if (this.children) {
                    params.children = this.children;
                }

                if (Object.keys(params).length) {
                    url += '?'+new URLSearchParams(params).toString();
                }
                return url;
            },

            stickyNavTopStyle() {
                if (this.uiStore.stickTopNavTop) {
                    return 'top: '+this.uiStore.stickTopNavTop+'px;';
                }
                return null;
            },

            stickyQuotePositionStyle() {
                if ((!this.uiStore.isMobile) && (!this.uiStore.isTablet)) {

                    let top = 50;
                    if (this.uiStore.stickTopNavTop) {
                        top += this.uiStore.stickTopNavTop;
                        
                    }
                    return 'top: '+top+'px;';
                }
                return null;
            },

            stickyQuotePoisitionCssClass() {
                if ((this.uiStore.isMobile) || (this.uiStore.isTablet)) {
                    return 'fixed-bottom rounded-0 rounded-top-2 bg-info bottom-safe-padding shadow';
                }
                else {
                    return 'sticky-top bg-light mt-3';
                }
            },

            showQuoteInNav() {
                if (
                    (!this.uiStore.isMobile) &&
                    (!this.uiStore.isTablet) &&
                    (this.navPosition) &&
                    (['overview', 'photos', 'amenities'].indexOf(this.navPosition) == -1)
                ) {
                    return true;
                }
                return false;
            },

            quoteFormButtonBlock() {
                if (this.uiStore.isTablet) {
                    return false;
                }
                return true;
            },

            arrivalDepartureLabelCssClass() {
                if ((this.uiStore.isMobile) || (this.uiStore.isTablet)) {
                    return 'x-small';
                }
                return 'text-primary small';
            },

            arrivalDepartureValueCssClass() {
                if ((this.uiStore.isMobile) || (this.uiStore.isTablet)) {
                    return 'small text-truncate';
                }
                return 'text-truncate';
            },

            arrivalDeparturePlaceholderCssClass() {
                if ((this.uiStore.isMobile) || (this.uiStore.isTablet)) {
                    return 'small';
                }
                return 'text-muted';
            },

            canReserve() {
                if (
                    (this.displayRates) &&
                    (this.quotes) &&
                    (this.property)
                ) {
                    // check if we have any quotes
                    let rates = Object.values(this.quotes);
                    for (let i=0; i<rates.length; i++) {
                        if (rates[i] != null) {
                            return true;
                        }
                    }
                }
                return false;
            },

            reserveButtonLabel() {
                if (this.useHotelRates) {
                    if (this.property.canBookEntireHotel) {
                        let hotelLabel = 'Hotel';
                        if (
                            (this.property.hotelSettings) &&
                            (this.property.hotelSettings.hotelTypeShortLabel)
                        ) {
                            hotelLabel = this.property.hotelSettings.hotelTypeShortLabel;
                        }
                        return 'Select Room/Entire '+hotelLabel;
                    }
                    return 'Select Room';
                }

                return this.reserveOrRequestLable;
            },
            
            multiCalReserveButtonLabel() {
                let prefix = '';
                if (
                    (!this.uiStore.isMobile) &&
                    (!this.uiStore.isTablet) &&
                    (this.canReserve) &&
                    (!this.useHotelRates)
                ) {
                    prefix  = this.money(this.quotes[this.property.id].total)+' / stay | ';
                }
                
                return prefix + this.reserveButtonLabel;
            },
            
            reserveOrRequestLable() {
                if (
                    (this.property) && 
                    (this.property.bookingRequest)
                ) {
                    return 'Request to Book';
                }
                if (this.thirdPartyRates) {
                    return 'Request to Book';
                }
                return 'Reserve Now';
            },

            avgHeaderLabel() {

                if (this.useHotelRates) {
                    let min;
                    let max;

                    if (
                        (this.property.canBookEntireHotel) &&
                        (this.property.schedulePriceAvg)
                    ) {
                        min = this.property.schedulePriceAvg;
                        max = this.property.schedulePriceAvg;
                    }

                    for (let i=0; i<this.property.liveHotelRooms.length; i++) {
                        if (this.property.liveHotelRooms[i].schedulePriceAvg) {
                            if ((!min) || (this.property.liveHotelRooms[i].schedulePriceAvg < min)) {
                                min = this.property.liveHotelRooms[i].schedulePriceAvg;
                            }
                            if ((!max) || (this.property.liveHotelRooms[i].schedulePriceAvg > max)) {
                                max = this.property.liveHotelRooms[i].schedulePriceAvg;
                            }
                        }
                    }

                    if ((min) && (max)) {
                        if (Math.round(min) == Math.round(max)) {
                            return this.money(min);
                        }
                        
                        return this.money(min)+'-'+this.money(max);
                    }
                }

                if (this.property.schedulePriceAvg) {
                    return this.money(this.property.schedulePriceAvg);
                }
                return null;
            },

            requestUrl() {
                if (
                    (this.$route.meta) &&
                    (this.$route.meta.usePreviewEndPoint)
                ) {
                    let re = 'properties/preview/'+this.$route.params.propertyId;
                    if (this.$route.params.hash) {
                        re += '/'+this.$route.params.hash;
                    }
                    return re;
                }

                let re = 'properties/view/'+this.$route.params.slug;
                if (this.$route.params.thirdParty) {
                    re += '/'+this.$route.params.thirdParty;
                }
                return re;
            },

            property() {
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    return null;
                }

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    if (
                        (!this.disposed) &&
                        (response.setMetaData) &&
                        (response.setMetaData instanceof Function)
                    ) {
                        response.setMetaData();
                    }

                    this.setMinMax(response.results);
                    this.setGuestsOptions(response.results);
                    this.recordEvent();

                    return response.results;
                }
                else {
                    let error = 'Could not complete request';
                    if (response.status == 404) {
                        this.showSimilar();
                        return;
                    }
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    this.setError(error);
                }
                
                return null;
            },

            thirdPartyRates() {
                if ((this.property) && (this.property.frontEndPartnersRate)) {
                    return this.property.frontEndPartnersRate;
                }
                return false;
            },

            inquireOnly() {
                if (this.property) {
                    if (this.property.status != 1) {
                        return true;
                    }

                    if ((!this.calMinDate) || (!this.calMaxDate)) {
                        return true;
                    }

                    if (
                        (this.property.isHotel) &&
                        (!this.property.canBookHotelRooms) &&
                        (!this.property.canBookEntireHotel)
                    ) {
                        return true;
                    }

                    return false;
                }
                return true;
            },

            showRoomsAndIcons() {
                if (
                    (this.property) &&
                    (this.property.isHotel) &&
                    (!this.property.canBookEntireHotel)
                ) {
                    return false;
                }
                return true;
            },

            useHotelRates() {
                if (
                    (this.property) &&
                    (this.property.isHotel) && 
                    (this.property.canBookHotelRooms) &&
                    (this.property.liveHotelRooms) &&
                    (this.property.liveHotelRooms.length)
                ) {
                    return true;
                }
                return false;
            },

            hotelShortLabel() {

                if (
                    (this.property) &&
                    (this.property.hotelSettings) &&
                    (this.property.hotelSettings.hotelTypeShortLabel)
                ) {
                    return this.property.hotelSettings.hotelTypeShortLabel;
                }

                return 'Hotel';
            },

            hotelRoomsLabel() {
                if (
                    (this.property) &&
                    (this.property.hotelSettings) &&
                    (this.property.hotelSettings.roomsLabel)
                ) {
                    return Utilities.ucFirst(this.property.hotelSettings.roomsLabel)+'s';
                }
                return 'Suites';
            },

            entireHotelRoomGroup() {
                if (
                    (this.property) &&
                    (this.property.canBookEntireHotel)
                ) {
                    let label = 'Entire Hotel';
                    if (
                        (this.property.hotelSettings) &&
                        (this.property.hotelSettings.entireHotelLabel)
                    ) {
                        label = this.property.hotelSettings.entireHotelLabel;
                    }

                    return {
                        label: label,
                        rooms: [this.property],
                        mainRoomId: this.property.id,
                        displayId: this.property.id,
                    };
                }
                return null;
            },

            // sort them by rate
            hotelRoomGroups() {

                if (
                    (this.property) &&
                    (this.property.liveHotelRooms) &&
                    (this.property.liveHotelRooms.length)
                ) {

                    let groups = {};
                    
                    for (let i=0; i<this.property.liveHotelRooms.length; i++) {

                        let gId = 'prop-'+this.property.liveHotelRooms[i].id;
                        let inGroup = false;

                        if (this.property.liveHotelRooms[i].hotelGroupId) {
                            gId = 'group-'+this.property.liveHotelRooms[i].hotelGroupId;
                            inGroup = true;
                        }

                        if (!groups[gId]) {
                                
                            // get the group name
                            let group = null;
                            if (
                                (inGroup) &&
                                (this.property.hotelRoomGroups) &&
                                (this.property.hotelRoomGroups.length)
                            ) {
                                for (let j=0; j<this.property.hotelRoomGroups.length; j++) {
                                    if (this.property.hotelRoomGroups[j].id == this.property.liveHotelRooms[i].hotelGroupId) {
                                        group = this.property.hotelRoomGroups[j];
                                        break;
                                    }
                                }
                            }
                            groups[gId] = {
                                group: group,
                                rooms: [],
                            };
                        }

                        groups[gId].rooms.push(this.property.liveHotelRooms[i]);
                    }

                    // add the main room id
                    for (const [gId, gData] of Object.entries(groups)) {

                        let mainId = gData.rooms[0].id;
                        if (
                            (gData.group) &&
                            (gData.group.mainRoomId)
                        ) {
                            // make sure the main room id is in the rooms
                            for (let i=0; i<gData.rooms.length; i++) {
                                if (gData.rooms[i].id == gData.group.mainRoomId) {
                                    mainId = gData.rooms[i].id;
                                    break;
                                }
                            }

                        }

                        let displayId = mainId;
                        let minRate;
                        if (this.displayRates) {

                            for (let i=0; i<gData.rooms.length; i++) {
                                if (
                                    (this.quotes) &&
                                    (this.quotes[gData.rooms[i].id]) &&
                                    (
                                        (!minRate) ||
                                        (this.quotes[gData.rooms[i].id].total < minRate)
                                    )
                                ) {
                                    minRate = this.quotes[gData.rooms[i].id].total;
                                    displayId = gData.rooms[i].id;
                                }
                            }
                        }

                        groups[gId].mainRoomId = mainId;
                        groups[gId].displayId = displayId;
                    }

                    // sorting the results by lowest rate
                    let re = Object.values(groups);
                    
                    if (this.displayRates) {
                        re.sort(function(a, b) {

                            // by default, move the none quotable to the end
                            let aQuote = Infinity;
                            let bQuote = Infinity;

                            if (
                                (this.quotes) &&
                                (this.quotes[a.displayId]) &&
                                (this.quotes[a.displayId].total < aQuote)
                            ) {
                                aQuote = this.quotes[a.displayId].total;
                            }
                            
                            if (
                                (this.quotes) &&
                                (this.quotes[b.displayId]) &&
                                (this.quotes[b.displayId].total < bQuote)
                            ) {
                                bQuote = this.quotes[b.displayId].total;
                            }

                            if (aQuote == bQuote) return 0;

                            return aQuote - bQuote;

                        }.bind(this));
                    }
                    // sort by the group sort
                    else {
                        re.sort(function(a, b) {
                            let aSort = 1000;
                            let bSort = 1000;
                            if ((a.group) && (a.group.sort != null)) {
                                aSort = a.group.sort;
                            }

                            if ((b.group) && (b.group.sort != null)) {
                                bSort = b.group.sort;
                            }

                            return aSort - bSort;

                        });
                    }

                    return re;
                
                }

                return null;
            },

            singleRangeCalProps() {
                return {
                    circleWedges: true,
                    min: this.calMinDate,
                    max: this.calMaxDate,
                    startLabel: 'Check-in',
                    startDateInstructions: 'Select Check-in Date',
                    endLabel: 'Check-out',
                    endDateInstructions: 'Select Check-out Date',
                    minDates: this.minStay,
                    slotsKey: this.calSlotKey+'',
                    date: this.calDisplayDate,
                    inline: true,
                    calendarProps: this.calendarProps
                };
            },

            singleRangeCalPropsInline() {
                return {...this.singleRangeCalProps, numberOfMonths: this.calFullNumberMonths};
            },

            guestsNumberLabel() {
                let lbl = (this.adults || 1)+' '+Formatter.pluralize((this.adults || 1), 'adults', 'adult');
                if (this.children) {
                    lbl += ' & '+this.children+' '+Formatter.pluralize((this.children || 1), 'children', 'child');
                }

                return lbl;
            },

            minStay() {
                return this.minStayByDate(this.calStartDate, this.property);
            },

            blockedDates() {
                if (
                    (this.property) &&
                    (this.calDisplayDate)
                ) {
                    let startDate = this.calDisplayDate.copy().startOfMonth();
                    let endDate = startDate.copy();
                    // we'll move 3 months since we display 3 months
                    // regardless if we are in desktop or mobile
                    endDate.month += this.calFullNumberMonths;

                    // if we have a start date, block
                    let blocked = [];
                    let softBlocked = {};

                    // we'll only add soft blocks if the previous date
                    // can be checked in
                    let lastDateOpen = false;

                    let loopDate = startDate.copy();
                    while(loopDate.time <= endDate.time) {

                        let hasCheckout = false;

                        // check-out = false
                        // date not open (unless the previous date is open and check-out is true)
                        if ((!this.calSelectedRange) && (this.calStartDate)) {

                            // for hotel, find any room that can checkout
                            if ((this.property.isHotel) && (this.property.canBookHotelRooms)) {
                                let foundCheckout = false;

                                if (this.property.canBookEntireHotel) {
                                    let canCheckOut = this.canCheckout(loopDate, this.property, true);
                                    if (canCheckOut == 1) {
                                        foundCheckout = true;
                                    }
                                    else if (canCheckOut == 0) {
                                        hasCheckout = true;
                                    }
                                }

                                if (
                                    (!foundCheckout) &&
                                    (this.property.liveHotelRooms) &&
                                    (this.property.liveHotelRooms.length)
                                ) {
                                    for (let i=0; i<this.property.liveHotelRooms.length; i++) {
                                        let canCheckOut = this.canCheckout(loopDate, this.property.liveHotelRooms[i], true);
                                        if (canCheckOut == 1) {
                                            foundCheckout = true;
                                        }
                                        else if (canCheckOut == 0) {
                                            hasCheckout = true;
                                        }
                                    }
                                }

                                if (!foundCheckout) {
                                    let loopDateCopy = loopDate.copy();
                                    loopDateCopy.ignoreComputedMinMax = hasCheckout;
                                    blocked.push(loopDateCopy);
                                }
                            }

                            // single property
                            else {
                                let canCheckOut = this.canCheckout(loopDate, this.property, true);
                                if (canCheckOut != 1) {
                                    let loopDateCopy = loopDate.copy();
                                    loopDateCopy.ignoreComputedMinMax = (canCheckOut == 0);
                                    blocked.push(loopDateCopy);
                                }
                            }
                        }
                        // block check-in = false or not open
                        else {
                            
                            // hotel find any room that can check-in
                            if ((this.property.isHotel) && (this.property.canBookHotelRooms)) {
                                let foundCheckin = false;
                                let isOpen = false;

                                // used to check open dates for soft block
                                let dateKey = loopDate.format('Ymd');

                                if (
                                    (lastDateOpen) &&
                                    (this.property.publicSchedules) &&
                                    (this.property.publicSchedules[dateKey])
                                ) {
                                    isOpen = true;
                                }

                                if (this.canCheckIn(loopDate, this.property)) {
                                    foundCheckin = true;
                                }

                                if (
                                    (!foundCheckin) &&
                                    (this.property.liveHotelRooms) &&
                                    (this.property.liveHotelRooms.length)
                                ) {
                                    for (let i=0; i<this.property.liveHotelRooms.length; i++) {
                                        if (this.canCheckIn(loopDate, this.property.liveHotelRooms[i])) {
                                            foundCheckin = true;
                                            break;
                                        }
                                        
                                        if (
                                            (lastDateOpen) &&
                                            (!isOpen) &&
                                            (this.property.liveHotelRooms[i].publicSchedules) &&
                                            (this.property.liveHotelRooms[i].publicSchedules[dateKey])
                                        ) {
                                            isOpen = true;
                                        }
                                    }
                                }

                                if (!foundCheckin) {
                                    
                                    // reset it the soft block last open
                                    lastDateOpen = false;

                                    blocked.push(loopDate.copy());
                                    if (isOpen) {
                                        
                                        // check if the date is currently selected

                                        if (
                                            (!this.calSelectedRange) ||
                                            (!this.calSelectedRange.inRange(loopDate))
                                        ) {
                                            let minStay = this.minStayByDate(loopDate, this.property, true);
                                            if (minStay) {
                                                softBlocked[loopDate.format('Y-m-d')] = minStay+'-night minimum';
                                            }
                                            else {
                                                softBlocked[loopDate.format('Y-m-d')] = 'Check-out only';
                                            }
                                        }
                                        
                                        // keep the range of open dates going for soft open dates
                                        lastDateOpen = true;
                                    }
                                }
                                else {
                                    lastDateOpen = true;
                                }

                            }

                            // single property
                            else {
                                if (!this.canCheckIn(loopDate, this.property)) {
                                    blocked.push(loopDate.copy());

                                    if (lastDateOpen) {
                                        
                                        // reset it the soft block last open
                                        lastDateOpen = false;

                                        let dateKey = loopDate.format('Ymd');
                                        
                                        if (
                                            (
                                                (this.property.publicSchedules) &&
                                                (this.property.publicSchedules[dateKey])
                                            ) ||
                                            (this.canCheckout(loopDate, this.property))
                                        ) {
                                            
                                            // check if the date is currently selected

                                            if (
                                                (!this.calSelectedRange) ||
                                                (!this.calSelectedRange.inRange(loopDate))
                                            ) {
                                                
                                                let minStay = this.minStayByDate(loopDate, this.property, true);
                                                if (minStay) {
                                                    softBlocked[loopDate.format('Y-m-d')] = minStay+'-night minimum';
                                                }
                                                else {
                                                    softBlocked[loopDate.format('Y-m-d')] = 'Check-out only';
                                                }
                                            }
                                            
                                            // keep the range of open dates going for soft open dates
                                            lastDateOpen = true;
                                        }
                                    }
                                    
                                }
                                else {
                                    lastDateOpen = true;
                                }
                                
                            }
                        }

                        loopDate.date += 1;
                    }

                    // check if we need to block a date range by max stay
                    if ((!this.calSelectedRange) && (this.calStartDate) && (this.calMaxDate)) {
                        let maxStay = this.maxStayByDate(this.calStartDate, this.property);
                        let maxDate = this.calStartDate.copy();
                        maxDate.date += maxStay;

                        if (maxDate.time < this.calMaxDate.time) {
                            maxDate.date += 1;
                            blocked.push(new DateRange(maxDate, this.calMaxDate.copy()));
                        }
                    }

                    if (blocked.length) {
                        return {
                            blocked: blocked,
                            softBlocked: softBlocked,
                        };
                    }
                }
                return null;
            },

            calendarProps() {
                if (this.blockedDates) {
                    return {
                        blockDates: this.blockedDates.blocked,
                        softBlockDates: this.blockedDates.softBlocked
                    };
                }
                return {};
            },

            galleryMedia() {
                let gallery = [];

                if (
                    (this.property) &&
                    (this.property.firstSlideVideo)
                ) {
                    let thumb = null;
                    if (
                        (this.property) &&
                        (this.property.photos) &&
                        (this.property.photos.length)
                    ) {
                        thumb = this.property.photos[0].galleryThumbSource;
                    }

                    let media = new Media(thumb, this.property.firstSlideVideo.embed, null, 'video');
                    media.name = 'Video'
                    gallery.push(media);
                }

                if (
                    (this.property) &&
                    (this.property.first3dTour)
                ) {
                    let media = new Media(null, this.property.first3dTour.embed, null, 'tour');
                    media.name = '3D Tour';
                    gallery.push(media);
                }

                if (
                    (this.property) &&
                    (this.property.photos) &&
                    (this.property.photos.length)
                ) {

                    for (let i=0; i<this.property.photos.length; i++) {
                        let thumb = this.property.photos[i].galleryThumbSource;
                        let src = this.property.photos[i].gallerySource || thumb;

                        let media = new Media(thumb, src, null, 'image');
                        media.title = this.property.photos[i].title;
                        media.description = this.property.photos[i].content;
                        media.setSize(this.property.photos[i].width, this.property.photos[i].height, {width:2000, height:1125});
                        gallery.push(media);
                    }
                    
                }

                if (
                    (this.property) &&
                    (this.property.publicExternalResources) &&
                    (this.property.publicExternalResources.length)
                ) {
                    for (let i=0; i<this.property.publicExternalResources.length; i++) {
                        let thumb = null;
                        let src = this.property.publicExternalResources[i].url;

                        let media = new Media(thumb, src);
                        media.title = this.property.publicExternalResources[i].name;
                        media.description = this.property.publicExternalResources[i].description;
                        media.data = {
                            floorPlan: true,
                        };
                        gallery.push(media);
                    }
                }

                if (gallery.length) {
                    return gallery;
                }

                return null;

            },

            showTruths() {
                if (
                    (this.property) &&
                    (this.property.truths) &&
                    (this.property.truths.length)
                ) {
                    return true;
                }
                return false;
            },

            hotelQuotesRangeLabel() {
                let arr = [];
                let vals = Object.values(this.quotes);
                for (let i=0; i<vals.length; i++) {
                    if ((vals[i]) && (vals[i].total)) {
                        arr.push(vals[i].total);
                    }
                }

                if (arr.length) {
                    if (arr.length == 1) {
                        return this.money(arr[0]);
                    }

                    arr.sort();

                    let min = arr[0];
                    let max = arr[arr.length - 1];
                    if (min == max) {
                        return this.money(min);
                    }

                    return this.money(min)+'-'+this.money(max);

                }
                return null;
            },

            mobileSelectedDatesLabel() {
                if (
                    (
                        (this.uiStore.isMobile) ||
                        (this.uiStore.isTablet)
                    ) &&
                    (this.calSelectedRange)
                ) {
                    return this.calSelectedRange.start.format('n/j/Y')+' - '+this.calSelectedRange.end.format('n/j/Y');
                }

                return null;
            },

            traditionalMonthsList() {
                if (
                    (this.property) &&
                    (this.property.traditional) &&
                    (this.calSelectedRange)
                ) {
                    let start = this.calSelectedRange.first; // creates a date copy
                    let end = this.calSelectedRange.last;
                    start.startOfMonth();
                    end.endOfMonth();

                    let months = [];
                    while (start.time <= end.time) {
                        months.push(start.format('F'));
                        start.month += 1;
                    }

                    if (months.length == 2) {
                        return months.join(' and ');
                    }
                    
                    if (months.length > 1) {
                        months[months.length - 1] = 'and '+months[months.length - 1];
                    }

                    return months.join(', ');
                }

                return null;
            },

        },

        methods: {

            setParamAndRequest() {
                this.setSearchFromParam();

                
                if ((!this.initialRequest) || (!this.property)) {
                    this.initialRequest = true;
                    this.requestData();
                }
            },

            setSearchFromParam() {

                if (this.$route.params.adults) {
                    let adults = parseFloat(this.$route.params.adults);
                    if (!isNaN(adults)) {
                        this.adults = adults;
                    }
                    else {
                        this.adults = 1;
                    }
                }
                else {
                    this.adults = 1;
                }

                if (this.$route.params.children) {
                    let children = parseFloat(this.$route.params.children);
                    if (!isNaN(children)) {
                        this.children = children;
                    }
                    else {
                        this.children = 0;
                    }
                }
                else {
                    this.children = 0;
                }

                if ((this.$route.params.start) && (this.$route.params.end)) {
                    let start = new DateObject(this.$route.params.start).startOfDay();
                    let end = new DateObject(this.$route.params.end).startOfDay();

                    if ((start.valid) && (end.valid) && (start.time < end.time)) {
                        if (!this.initialRequest) {
                            this.calDisplayDate = start.copy().startOfMonth();
                        }
                        this.calSelectedRange = new DateRange(start, end);
                    }
                    else {
                        if (!this.initialRequest) {
                            this.calDisplayDate = null;
                        }
                        this.calSelectedRange = null;
                    }
                }
                else {
                    if (!this.initialRequest) {
                        this.calDisplayDate = null;
                    }
                    this.calSelectedRange = null;
                }

                this.calculateRates();
            },

            money(amount) {
                return Formatter.money(amount, 0);
            },

            setNavPosition(position) {
                this.navPosition = position;
                
                requestAnimationFrame(function() {
                    if (this.uiStore.isMobile || this.uiStore.isTablet) {
                        let elm = this.$refs.propertyNav.querySelector('.active');
                        var nav = this.$refs.propertyNav.querySelector('.nav');

                        if ((elm) && (nav)) {
                            UI.hScrollToChild(nav, elm, false, 'center');
                        }
                    }
                }.bind(this));
            },

            moveToSection(position, selector) {
                let elm = this.$refs[position+'Container'];
                if (elm) {
                    let offset = 100;
                    if (selector) {
                        let child = elm.querySelector(selector);
                        if (child) {
                            elm = child;
                            offset = 150;
                        }
                    }
                    let y = elm.getBoundingClientRect().top + window.scrollY - offset;
                    this.uiStore.scrollWindow(y);
                }
            },

            openGallery(index) {
                if (!index) {
                    index = 0;
                }

                this.activeGallery = index;
                this.showGallery = true;
            },

            setMinMax(property) {
                let min = null;
                let max = null;
                
                if ((property.isHotel) && (property.canBookHotelRooms)) {
                    if (property.canBookEntireHotel) {
                        let keys = Object.keys(property.publicSchedules);
                        if ((keys) && (keys.length > 1)) {
                            min = DateObject.fromYmd(keys[0]);
                            max = DateObject.fromYmd(keys[keys.length - 1]);
                        }
                    }

                    if ((property.liveHotelRooms) && (property.liveHotelRooms.length)) {
                        for (let i=0; i<property.liveHotelRooms.length; i++) {
                            if (property.liveHotelRooms[i].publicSchedules) {
                                let keys = Object.keys(property.liveHotelRooms[i].publicSchedules);

                                if ((keys) && (keys.length > 1)) {
                                    let roomMin = DateObject.fromYmd(keys[0]);
                                    let roomMax = DateObject.fromYmd(keys[keys.length - 1]);

                                    if ((!min) || (roomMin.time < min.time)) {
                                        min = roomMin;
                                    }
                                    if ((!max) || (roomMax.time > max.time)) {
                                        max = roomMax;
                                    }
                                }
                            }
                        }
                    }
                }
                else {
                    if (property.publicSchedules) {
                        let keys = Object.keys(property.publicSchedules);
                        if ((keys) && (keys.length > 1)) {
                            min = DateObject.fromYmd(keys[0]);
                            max = DateObject.fromYmd(keys[keys.length - 1]);
                        }
                    }
                }

                if (min) {
                    
                    let checkUntil = min.copy();
                    checkUntil.year += 1;
                    if ((max) && (checkUntil.time > max.time)) {
                        checkUntil = max.copy();
                    }

                    if ((property.isHotel) && (property.canBookHotelRooms)) {

                        // eslint-disable-next-line
                        while (true) {
                            if (property.canBookEntireHotel) {
                                if (this.canCheckIn(min, property)) {
                                    break;
                                }
                            }

                            let foundRoomCheckin = false;

                            for (let i=0; i<property.liveHotelRooms.length; i++) {
                                if (this.canCheckIn(min, property.liveHotelRooms[i])) {
                                    foundRoomCheckin = true;
                                    break;
                                }
                            }

                            if (foundRoomCheckin) {
                                break;
                            }

                            min.date += 1;
                            if (min.time >= checkUntil.time) {
                                break;
                            }
                        }
                    }
                    else {
                        while (!this.canCheckIn(min, property)) {
                            min.date += 1;
                            if (min.time >= checkUntil.time) {
                                break;
                            }
                            
                        }
                    }
                }

                // we need to add one day to the max
                // so if the calendar ends at the last
                // date, check-out is allowed
                if (max) {
                    max.date += 1;
                }
                
                this.calMinDate = min;
                this.calMaxDate = max;
                if (min) {
                    if (
                        (!this.calDisplayDate) ||
                        (this.calDisplayDate.time < min.time)
                    ) {
                        this.calDisplayDate = min.copy();
                    }
                }

            },

            setGuestsOptions(property) {
                let maxOccupancy = 12;
                if ((property) && (property.maxOccupancy)) {
                    maxOccupancy = property.maxOccupancy;
                }
                let adults = [];
                let children = [];
                for (let i=0; i<=maxOccupancy; i++) {
                    if (i > 0) {
                        adults.push(new FormOption(i, i+' '+Formatter.pluralize(i, 'adults', 'adult')));
                    }
                    children.push(new FormOption(i, i+' '+Formatter.pluralize(i, 'children', 'child')));
                }

                this.numberAdultsOptions = adults;
                this.numberChildrenOptions = children;
            },

            updateDisplayMonth(month) {
                this.calDisplayDate = month;
            },

            clearSelectedDate() {
                this.calStartDate = null;
                this.calSelectedRange = null;
                this.calSlotKey++;
            },

            setStartDate(date) {
                this.calStartDate = date;
            },
            onEndDate() {
                this.calPeekOpen = false;
            },

            canCheckIn(date, property) {
                if (!property.publicSchedules) {
                    return false;
                }

                let dateKey = date.format('Ymd');

                if (
                    (property.publicSchedules[dateKey]) &&
                    (property.publicSchedules[dateKey].i)
                ) {
                    
                    // check if we can book up to the min stay
                    // remove one day because we are starting
                    // the loop from the second date
                    let minStay = this.minStayByDate(date, property) - 1;

                    // if the max stay is below the min stay
                    // can't check in
                    let maxStay = this.maxStayByDate(date, property);
                    if (maxStay < (minStay + 1)) { // we are removing 1 initiall
                        return false;
                    }

                    let loopDate = date.copy();
                    loopDate.date += 1;
                    for (let i=0; i<minStay; i++) {
                        let key = loopDate.format('Ymd');
                        if (!property.publicSchedules[key]) {
                            return false;
                        }
                        loopDate.date += 1;
                    }
                    return true;
                }

                return false;
            },

            /**
             * @return {Bool|Number} bool if returnType is true
             *  Number: 0 = No Checkout, 1 = Can checkout,  -1 = no date
             */ 
            canCheckout(date, property, returnType) {
                if (!property.publicSchedules) {
                    if (returnType) {
                        return -1;
                    }
                    return false;
                }

                let dateKey = date.format('Ymd');
                // no date, check if the previous date was open
                // and check-out is okay
                if (!property.publicSchedules[dateKey]) {
                    let preDate = date.copy();
                    preDate.date -= 1;
                    let preDateKey = preDate.format('Ymd');

                    if (returnType) {
                        if (!property.publicSchedules[preDateKey]) {
                            return -1;
                        }
                        if (!property.publicSchedules[preDateKey].o) {
                            return 0;
                        }
                    }
                    else {

                        if (
                            (!property.publicSchedules[preDateKey]) ||
                            (!property.publicSchedules[preDateKey].o)
                        ) {
                            return false;
                        }
                    }
                    
                }
                else if (!property.publicSchedules[dateKey].o) {
                    if (returnType) {
                        return 0;
                    }
                    else {
                        return false;
                    }
                }

                if (returnType) {
                    return 1;
                }

                return true;
            },

            async requestData() {
                if (this.disposed) return;
                
                this.setError(null);
                this.setEmptyResults(null);
                
                clearTimeout(this.requestsTimer);

                await this.apiStore.get(this.requestUrl);

                this.requestsTimer = setTimeout(this.requestData, ApiConfig.dashboardCache * 1000);
            },

            setError(error) {
                this.error = error;
            },

            setEmptyResults(val) {
                this.emptyResults = val;
            },

            showSimilar() {
                if (
                    (this.$route.meta) &&
                    (this.$route.meta.usePreviewEndPoint)
                ) {
                    this.error = 'Property Not Found';
                    return;
                }
                this.$router.replace('/similar-properties/'+this.$route.params.slug);
            },

            async calculateRates() {

                if ((!this.authStore.initialPull) || (!this.property)) {
                    setTimeout(this.calculateRates, 500);
                    return;
                }

                let resetData = true;
                let quoteKey;
                if (this.calSelectedRange) {
                    quoteKey = this.calSelectedRange.start.format('Y-m-d')+'-'+this.calSelectedRange.end.format('Y-m-d');
                    if (this.lastQuoteKey == quoteKey) {
                        resetData = false;
                    }
                }

                if (resetData) {
                    this.displayRates = false;
                    this.quotes = null;
                    this.quotesCheckIn = null;
                    this.quotesCheckOut = null;
                }

                if (this.calSelectedRange) {

                    
                    if (resetData) {
                        this.loadingQuotes = true;
                    }

                    let data = {
                        start: this.calSelectedRange.start.format('Y-m-d'),
                        end: this.calSelectedRange.end.format('Y-m-d'),
                        adults: this.adults,
                        children: this.children,
                        thirdPartyRates: this.thirdPartyRates,
                    };

                    let response = await Api.post('properties/'+this.property.id+'/quote', data);

                    if ((response.valid) && (response.results) && (!response.results.error)) {
                        
                        this.displayRates = true;
                        this.quotes = response.results.quotes;
                        this.quotesCheckIn = response.results.start;
                        this.quotesCheckOut = response.results.end;

                        if ((!this.quoteFromCreated) && (this.useHotelRates)) {
                            requestAnimationFrame(function() {
                                this.moveToSection('hotelRooms', '.card:not(.disabled)');
                            }.bind(this));
                        }

                        if (this.thirdPartyRates) {
                            // block the property from public search
                            let blockPropertiesFromSearch = MobileApp.getSessionItem('blockPropertiesFromSearch');
                            if (!blockPropertiesFromSearch) {
                                blockPropertiesFromSearch = [];
                            }
                            
                            blockPropertiesFromSearch.push(this.property.id);
                            MobileApp.setSessionItem('blockPropertiesFromSearch', blockPropertiesFromSearch);
                        }
                    }
                    else {
                        
                        this.displayRates = false;
                        this.quotes = null;
                        this.quotesCheckIn = null;
                        this.quotesCheckOut = null;
                        quoteKey = null;

                        // close the occupants count
                        this.occupantsPeekOpen = false;

                        let error = 'Could not complete request';
                        if ((response.results) && (response.results.error)) {
                            if (response.results.error.message) error = response.results.error.message;
                            else error = response.results.error;
                        }
                        
                        if (!this.quoteFromCreated) {
                            this.uiStore.toast({
                                error: true,
                                title: 'Could not create quote',
                                message: error,
                            });
                        }
                    }

                    this.loadingQuotes = false;
                }

                this.lastQuoteKey = quoteKey;

                // reset the flag
                this.quoteFromCreated = false;
            },

            reserveButtonClick() {
                if (this.canReserve) {

                    if (this.useHotelRates) {
                        this.moveToSection('hotelRooms', '.card:not(.disabled)');
                    }
                    else {
                        this.startReservation();
                    }
                }
                else {
                    this.calPeekOpen = true;
                }
            },

            async startReservation(property) {
                
                let data = {
                    start: this.calSelectedRange.start.copy(),
                    end: this.calSelectedRange.end.copy(),
                    adults: this.adults,
                    children: this.children,
                };

                if (this.thirdPartyRates) {
                    data.thirdPartyRates = this.$route.params.thirdParty;
                }

                if (!property) property = this.property;

                this.buttonsLoading = true;
                
                let response = await Api.post('properties/'+property.id+'/start-booking', data);

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    this.apiStore.addRequest({
                        url: this.thirdPartyRates ? `properties/lease/${this.$route.params.thirdParty}` : 'properties/book',
                        response: response
                    });

                    Analytics.event('AddToCart', {
                        content_category: 'property',
                        content_name: this.property.name,
                        content: [{id: this.property.id, quantity:1}],
                    });
                    
                    this.$router.push(this.thirdPartyRates ? `/lease/${this.$route.params.thirdParty}` : '/book');
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    
                    this.uiStore.toast({
                        error: true,
                        title: 'Could not create quote',
                        message: error,
                    });
                }

                this.buttonsLoading = false;
            },

            onGuestsNumberClose() {
                // reset the open status
                this.occupantsPeekOpen = null;
            },

            onCalPeekClose() {
                // reset the open status
                this.calPeekOpen = null;
            },

            toggleFeesList() {
                if (this.showQuoteDetails) {
                    this.closeFeesList();
                }
                else {
                    this.showFeesList();
                }
            },

            showFeesList() {
                this.quoteDetailsOpen = true;
                this.showQuoteDetails = true;
            },

            closeFeesList() {
                this.showQuoteDetails = false;
                this.quoteDetailsOpen = true;
            },

            recordEvent() {
                if (!this.eventRecorded) {
                    this.eventRecorded = true;
                    // run it on delay so the property data is ready
                    requestAnimationFrame(function() {
                        Analytics.event('ViewContent', {
                            content_category: 'property',
                            content_ids: [this.property.id],
                            content_name: this.property.name,
                        });

                        if (this.property.city) {
                            Analytics.event('ViewContent', {
                                content_category: 'city',
                                content_name: this.property.city,
                            });
                        }

                        if (this.property.bedrooms) {
                            Analytics.event('ViewContent', {
                                content_category: 'bedrooms',
                                content_name: this.property.bedrooms,
                            });
                        }
                    }.bind(this));
                }
            },

            minStayByDate(date, property, null_on_default) {
                if (
                    (date) &&
                    (property)
                ) {
                    let key = date.format('Ymd');

                    if ((property.isHotel) && (property.canBookHotelRooms)) {

                        // find the lowest min stay for any room

                        let min;

                        if (property.canBookEntireHotel) {
                            if (
                                (property.publicSchedules) &&
                                (property.publicSchedules[key])
                            ) {
                                min = property.publicSchedules[key].m;
                            }
                        }

                        if (
                            (property.liveHotelRooms) &&
                            (property.liveHotelRooms.length)
                        ) {
                            for (let i=0; i<property.liveHotelRooms.length; i++) {

                                if (
                                    (property.liveHotelRooms[i].publicSchedules) &&
                                    (property.liveHotelRooms[i].publicSchedules[key])
                                ) {

                                    if ((!min) || (property.liveHotelRooms[i].publicSchedules[key].m < min)) {
                                        min = property.liveHotelRooms[i].publicSchedules[key].m;
                                    }
                                }
                                
                            }
                        }

                        if (min) {
                            return min;
                        }
                    }
                    else {
                        if (
                            (property.publicSchedules) &&
                            (property.publicSchedules[key])
                        ) {
                            return property.publicSchedules[key].m;
                        }
                    }
                }
                if (null_on_default) return null;
                return 2;
            },

            maxStayByDate(date, property, null_on_default) {
                if (
                    (date) &&
                    (property)
                ) {
                    let key = date.format('Ymd');

                    if ((property.isHotel) && (property.canBookHotelRooms)) {

                        // find the lowest max stay for any room

                        let max;

                        if (property.canBookEntireHotel) {
                            if (
                                (property.publicSchedules) &&
                                (property.publicSchedules[key])
                            ) {
                                max = property.publicSchedules[key].x;
                            }
                        }

                        if (
                            (property.liveHotelRooms) &&
                            (property.liveHotelRooms.length)
                        ) {
                            for (let i=0; i<property.liveHotelRooms.length; i++) {

                                if (
                                    (property.liveHotelRooms[i].publicSchedules) &&
                                    (property.liveHotelRooms[i].publicSchedules[key])
                                ) {

                                    if ((!max) || (property.liveHotelRooms[i].publicSchedules[key].x < max)) {
                                        max = property.liveHotelRooms[i].publicSchedules[key].x;
                                    }
                                }
                                
                            }
                        }

                        if (max) {
                            return max;
                        }
                    }
                    else {
                        if (
                            (property.publicSchedules) &&
                            (property.publicSchedules[key])
                        ) {
                            return property.publicSchedules[key].x;
                        }
                    }
                }
                if (null_on_default) return null;
                return 365;
            },

            leftPageVisibility() {
                this.leftPage((document.visibilityState == 'hidden'));
            },

            leftPage(hidden) {
                if (this.property) {
                    if (hidden == null) hidden = true;
                    Api.get('properties/viewing/'+this.property.id+'?hidden='+(hidden ? 1 : 0));
                }
            },
        }
    }
</script>